/**
 * The theme's videos (design spec, "Size video", "Product details video" and "Videos on the home page"), loaded on
 * every page by layout/theme.liquid:
 * - window.Miscela.video: load, play and pause a light <video>, in the smaller file its playlist names
 *   (snippets/video-file.liquid)
 * - <video-bubble>: the round bubble (sizes on shirt pages, welcome on the home page; snippets/video-bubble.liquid)
 * - <video-card>: a card that plays its video muted while on screen (home page; snippets/video-card.liquid)
 * Both open their video full screen with sound (snippets/video-dialog.liquid).
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DAY = 24 * 60 * 60 * 1000;

  // The files of a video, by height, as its HLS master playlist names them (snippets/video-file.liquid): Shopify keeps a
  // 480p and a 720p MP4 beside the 1080p that Liquid lists, named with the video's own bitrate, which only the playlist
  // tells. It lists each one as `<id>.HD-720p.4.5Mbps.hls.<n>_.m3u8` under `RESOLUTION=404x720`, i.e. the MP4
  // `<id>.HD-720p-4.5Mbps-<n>.mp4` in the same folder (checked on the store, 2026-10-22). Read once per video.
  const playlists = new Map();
  const known = new Map();
  const filesOf = (playlistUrl) => {
    if (!playlists.has(playlistUrl)) {
      const base = new URL(playlistUrl, window.location.href);
      const read = fetch(base)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((text) => {
          const files = new Map();
          const lines = text.split(/\r?\n/);
          lines.forEach((line, index) => {
            const resolution = line.startsWith('#EXT-X-STREAM-INF') && line.match(/RESOLUTION=\d+x(\d+)/);
            const name = resolution && (lines[index + 1] || '').trim().match(/^(.+)\.((?:SD|HD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/);
            if (name) files.set(Number(resolution[1]), new URL(`${name[1]}.${name[2]}-${name[3]}-${name[4]}.mp4`, base).href);
          });
          known.set(playlistUrl, files);
          return files;
        });
      playlists.set(playlistUrl, read);
    }
    return playlists.get(playlistUrl);
  };

  // Sets a video's file; one other than the listed file (`data-src`) falls back to it if it fails to load, and plays
  // again there if it is meant to be playing.
  const setFile = (video, url) => {
    const listed = video.dataset.src;
    if (url !== listed) {
      video.addEventListener(
        'error',
        () => {
          video.src = listed;
          if (video.dataset.playing) video.play()?.catch(() => {});
        },
        { once: true }
      );
    }
    video.src = url;
  };

  // Loads a video's file: the one of the height wanted (`data-height`) that its playlist names (`data-hls`), else the
  // listed file. Plays it once there if it is still meant to be playing: a card that left the screen while the
  // playlist was on its way stays paused.
  const load = (video) => {
    if (video.getAttribute('src') || video.dataset.loading) return;
    const use = (url) => {
      delete video.dataset.loading;
      setFile(video, url);
      if (video.dataset.playing) video.play()?.catch(() => {});
    };
    if (!video.dataset.hls) {
      use(video.dataset.src);
      return;
    }
    video.dataset.loading = '1';
    filesOf(video.dataset.hls).then(
      (files) => use(files.get(Number(video.dataset.height)) || video.dataset.src),
      () => use(video.dataset.src)
    );
  };

  // Loads a video's file without waiting: the one of the height wanted if its playlist has been read already (for the
  // preview), else the listed file. The full-screen video starts inside the tap, the only way iPhones allow its sound.
  const loadNow = (video) => {
    if (video.getAttribute('src')) return;
    const files = video.dataset.hls && known.get(video.dataset.hls);
    setFile(video, (files && files.get(Number(video.dataset.height))) || video.dataset.src);
  };
  const play = (video) => {
    video.dataset.playing = '1';
    if (video.getAttribute('src')) video.play()?.catch(() => {});
    else load(video);
  };
  const pause = (video) => {
    delete video.dataset.playing;
    video.pause();
  };

  window.Miscela = window.Miscela || {};
  window.Miscela.video = { load, play, pause };

  // The video full screen: with sound from the start (a browser that refuses it even after the tap plays it muted, and
  // the button then offers the sound), closed by its button, Escape or a tap outside it; closing stops it.
  class VideoDialog {
    constructor(dialog, onClose) {
      this.dialog = dialog;
      this.player = dialog.querySelector('.video-dialog__player');
      this.muteButton = dialog.querySelector('[data-video-mute]');
      dialog.querySelector('[data-video-close]').addEventListener('click', () => dialog.close());
      this.muteButton.addEventListener('click', () => this.setMuted(!this.player.muted));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        if (this.player) pause(this.player);
        onClose();
      });
    }

    get isOpen() {
      return this.dialog.open;
    }

    open() {
      this.dialog.showModal();
      if (!this.player) return;
      loadNow(this.player);
      this.player.currentTime = 0;
      this.player.dataset.playing = '1';
      this.setMuted(false);
      this.player.play()?.catch(() => {
        this.setMuted(true);
        this.player.play()?.catch(() => {});
      });
    }

    close() {
      this.dialog.close();
    }

    setMuted(muted) {
      if (!this.player) return;
      this.player.muted = muted;
      this.muteButton.textContent = muted ? 'Ligar o som' : 'Tirar o som';
    }
  }

  // The bubble plays its preview muted once the page has loaded (still frame for reduced motion, paused while the tab
  // is hidden); a tap opens the video with sound; the × hides it — for the visit, or for `data-remember-days` on the
  // device. With `data-hide-after-watch` opening the video counts as the ×, and the bubble goes once it is closed.
  class VideoBubble extends HTMLElement {
    connectedCallback() {
      if (this.remembered()) return;
      this.preview = this.querySelector('[data-video-preview]');
      this.dialog = new VideoDialog(this.querySelector('[data-video-dialog]'), () => this.afterClose());
      this.hidden = false;

      this.querySelector('[data-video-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-video-dismiss]').addEventListener('click', () => this.dismiss());
      this.querySelector('[data-video-choose]')?.addEventListener('click', () => this.chooseSize());
      this.onVisibility = () => (document.hidden ? this.pausePreview() : this.playPreview());
      document.addEventListener('visibilitychange', this.onVisibility);

      if (document.readyState === 'complete') this.playPreview();
      else window.addEventListener('load', () => this.playPreview(), { once: true });
    }

    disconnectedCallback() {
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get days() {
      return Number(this.dataset.rememberDays) || 0;
    }

    // Storage can be blocked (private mode): the bubble then simply shows, and a × hides it for this page view only.
    remembered() {
      try {
        const key = this.dataset.rememberKey;
        if (!this.days) return Boolean(window.sessionStorage.getItem(key));
        return Date.now() < Number(window.localStorage.getItem(key));
      } catch (err) {
        return false;
      }
    }

    remember() {
      try {
        const key = this.dataset.rememberKey;
        if (this.days) window.localStorage.setItem(key, String(Date.now() + this.days * DAY));
        else window.sessionStorage.setItem(key, '1');
      } catch (err) {
        // Not remembered.
      }
    }

    playPreview() {
      if (!this.preview || this.hidden || this.dialog.isOpen || document.hidden || reducedMotion.matches) return;
      this.preview.muted = true;
      play(this.preview);
    }

    pausePreview() {
      if (this.preview) pause(this.preview);
    }

    open() {
      this.pausePreview();
      this.dialog.open();
      if (this.hasAttribute('data-hide-after-watch')) {
        this.watched = true;
        this.remember();
      }
    }

    afterClose() {
      if (this.watched) this.hidden = true;
      else this.playPreview();
    }

    chooseSize() {
      this.dialog.close();
      const options = document.querySelector('.product-option');
      if (!options) return;
      options.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      options.querySelector('input:not([disabled]), button:not([disabled])')?.focus({ preventScroll: true });
    }

    dismiss() {
      this.pausePreview();
      this.hidden = true;
      this.remember();
    }
  }

  // The card plays its preview muted only while most of it is on screen (and the tab is shown), and pauses when it
  // leaves; nothing of the video loads before that. Reduced motion: the cover stays still. A tap opens the video with
  // sound.
  class VideoCard extends HTMLElement {
    connectedCallback() {
      this.preview = this.querySelector('[data-video-preview]');
      this.dialog = new VideoDialog(this.querySelector('[data-video-dialog]'), () => this.playPreview());
      this.querySelector('[data-video-open]').addEventListener('click', () => {
        this.pausePreview();
        this.dialog.open();
      });
      if (!this.preview || !('IntersectionObserver' in window)) return;
      this.preview.addEventListener('playing', () => this.classList.add('is-playing'));
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.onScreen = entry.isIntersecting;
            if (this.onScreen) this.playPreview();
            else this.pausePreview();
          });
        },
        { threshold: 0.6 }
      );
      this.observer.observe(this);
      this.onVisibility = () => (document.hidden ? this.pausePreview() : this.playPreview());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    playPreview() {
      if (!this.preview || !this.onScreen || this.dialog.isOpen || document.hidden || reducedMotion.matches) return;
      this.preview.muted = true;
      play(this.preview);
    }

    // Also while its file is still being chosen (the playlist on its way), so it does not start once that arrives.
    pausePreview() {
      if (this.preview && (this.preview.getAttribute('src') || this.preview.dataset.playing)) pause(this.preview);
    }
  }

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('video-bubble', VideoBubble);
  define('video-card', VideoCard);
})();
